import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Field } from "formik";
import { get } from "lodash";
import Modal from "@/components/Modal";
import EntityForm from "@/modules/entity/forms";
import { Fields } from "@/uicomponents";
import { CalendarClock } from "lucide-react";
import api from "@/services/api";
import useAlert from "@/hooks/useAlert";
import { buildOrganizationFields } from "./organizationFields";

const ORG_INCLUDE = "restoredUser";
const ORG_APPEND = "state,restored_status";

const MODE_CONFIG = {
  install: {
    title: "Дата установки",
    fields: ["state_id", "installation_date"],
  },
  restored: {
    title: "Дата восстановления",
    fields: ["restored_at", "restored_by", "restored_status_id"],
  },
};

const renderField = (key) => {
  switch (key) {
    case "installation_date":
      return (
        <Field
          key={key}
          component={Fields.NewDatePicker}
          name="installation_date"
          label="Дата установки"
          format="dd.MM.yyyy"
          isRequired={false}
          inputClassname="!h-[52px] !rounded-[15px]"
        />
      );
    case "restored_at":
      return (
        <Field
          key={key}
          component={Fields.NewDatePicker}
          name="restored_at"
          label="Дата восстановления"
          format="dd.MM.yyyy"
          isRequired={false}
          inputClassname="!h-[52px] !rounded-[15px]"
        />
      );
    case "state_id":
      return (
        <Field
          key={key}
          component={Fields.NewAsyncSelectS1}
          name="state_id"
          placeholder="Выберите статус"
          label="Статус"
          size="large"
          optionLabel="name"
          optionValue="id"
          loadOptionsUrl="/organization-states"
          isSearchable={true}
        />
      );
    case "restored_by":
      return (
        <Field
          key={key}
          component={Fields.NewAsyncSelectS1}
          name="restored_by"
          placeholder="Выберите кем восстановлено"
          label="Кем восстановлено"
          size="large"
          optionLabel="name"
          loadOptionsUrl="/user"
          isSearchable={true}
          loadOptionsParams={(search) => ({
            filter: { "role.role": "sales" },
            extra: { name: search },
          })}
        />
      );
    case "restored_status_id":
      return (
        <Field
          key={key}
          component={Fields.NewAsyncSelectS1}
          name="restored_status_id"
          placeholder="Выберите статус"
          label="Статус восстановления"
          size="large"
          optionLabel="name"
          optionValue="id"
          loadOptionsUrl="/organization-restored-statuses"
          isSearchable={true}
        />
      );
    default:
      return null;
  }
};

const QuickEditFieldsModal = ({ isOpen, toggle, orgId, mode, onSuccess }) => {
  const { showAlert } = useAlert();
  const config = MODE_CONFIG[mode] || MODE_CONFIG.install;

  const { data, isFetching } = useQuery({
    queryKey: ["ical-quick-edit-org", orgId],
    queryFn: () =>
      api.request.get(`/employee/organizations/${orgId}`, {
        params: { include: ORG_INCLUDE, append: ORG_APPEND },
      }),
    enabled: isOpen && !!orgId,
  });

  const selected = useMemo(() => {
    return get(data, "data.data") || get(data, "data") || null;
  }, [data]);

  const fields = useMemo(() => {
    if (!selected) return [];
    const all = buildOrganizationFields(selected);
    return all.filter((f) => config.fields.includes(f.name));
  }, [selected, config.fields]);

  return (
    <Modal isOpen={isOpen} toggle={toggle} size={500} zIndex={1070}>
      <div className="ical-edit-org">
        <div className="ical-title">
          <span className="ical-title__icon">
            <CalendarClock size={16} />
          </span>
          <span className="ical-title__text">{config.title}</span>
        </div>

        {isFetching || !selected ? (
          <div className="ical-empty">
            <div className="ical-spinner" />
          </div>
        ) : (
          <EntityForm.Main
            method="put"
            entity="organizations"
            name="all"
            url={`/employee/organizations/${orgId}`}
            normalizeData={(d) => d}
            id={orgId}
            updateData={true}
            selfErrorMessage={true}
            onSuccess={() => {
              showAlert("Сохранено", { type: "success", delay: 1000 });
              if (onSuccess) onSuccess();
            }}
            onError={(errors) => {
              let errorMessage = "Xato! Iltimos qaytadan urinib ko'ring";
              if (errors && errors.message) {
                errorMessage = errors.message;
              } else if (errors && errors.errors) {
                const firstField = Object.keys(errors.errors)[0];
                if (firstField) errorMessage = errors.errors[firstField][0];
              }
              showAlert(errorMessage, { type: "danger" });
            }}
            fields={fields}
          >
            {({ isSubmitting }) => (
              <div className="ical-edit-org__form">
                <div className="ical-edit-org__row ical-edit-org__row--three">
                  {config.fields.map((key) => renderField(key))}
                </div>

                <div className="ical-edit-org__actions">
                  <button
                    type="button"
                    className="ical-edit-org__cancel"
                    onClick={toggle}
                  >
                    Отмена
                  </button>
                  <button
                    type="submit"
                    className="ical-set-btn"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Сохранение..." : "Сохранить"}
                  </button>
                </div>
              </div>
            )}
          </EntityForm.Main>
        )}
      </div>
    </Modal>
  );
};

export default QuickEditFieldsModal;
